/**
 * What `user_connections.is_active` actually means, in one place.
 *
 * THE PROBLEM IT SOLVES. `is_active` is a single boolean carrying at least four
 * distinct situations, and every reader collapsed them back down to "connected
 * or not":
 *
 * | Who writes `is_active = false`                                | Real meaning              |
 * | ------------------------------------------------------------- | ------------------------- |
 * | `supabase/functions/shopify-webhooks` (`app/uninstalled`)      | provider revoked our token |
 * | `supabase/functions/webhook-tiktok` (revocation webhook)       | provider revoked our token |
 * | `supabase/functions/enhanced-platform-disconnect`              | the user disconnected      |
 * | a reconnect (no unique key on `(user_id, platform)` — the old  | superseded by a newer row  |
 * | row is left behind while a new row is inserted)                |                            |
 *
 * Measured on prod 2026-07-31: `test@example.com` has a Shopify row with
 * `is_active = false` for `javacityroasters.myshopify.com` **and 3 synced
 * products sitting in `shopify_products`**; clayton has FIVE rows for
 * `winterbananza.myshopify.com`, four of them superseded. `ProductService`
 * filtered on `.eq('is_active', true)` and returned `null`, so the product
 * picker rendered "Connect Shopify — link your Shopify store", which is false:
 * the store is linked, its data is right there, and the token is what lapsed.
 * The merchant is told to do the one thing they already did.
 *
 * THE RULE.
 * 1. Rows are per (user, platform, shop) and are NOT unique — always resolve
 *    over the whole set, newest `updated_at` first. Never `.limit(1)` on an
 *    unordered query.
 * 2. If any row is active, the connection is `active`. A newer inactive row
 *    never demotes a live one.
 * 3. Otherwise the newest inactive row decides, and it is `disconnected` — we
 *    know the shop, we may still hold its last-synced data, and the fix is
 *    "reconnect", not "connect".
 * 4. No rows at all is `never_connected` — the ONLY state for which
 *    "Connect Shopify" is a true sentence.
 *
 * `disconnected` and `never_connected` must stay distinguishable all the way to
 * the UI. Collapsing them is what produced the false empty state.
 */

export type PlatformConnectionState = 'active' | 'disconnected' | 'never_connected';

export interface ConnectionRowLike {
  is_active: boolean | null;
  /** Shop domain / handle, e.g. `javacityroasters.myshopify.com`. */
  platform_user_id?: string | null;
  updated_at?: string | null;
}

export interface ResolvedConnection<TRow extends ConnectionRowLike> {
  state: PlatformConnectionState;
  /** The row the state was decided from; `null` only when there are no rows. */
  row: TRow | null;
  /** Extra rows for the same platform — superseded reconnects, safe to ignore. */
  supersededCount: number;
}

function updatedAtMs(row: ConnectionRowLike): number {
  const t = row.updated_at ? Date.parse(row.updated_at) : NaN;
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Resolve every `user_connections` row for one platform into a single state.
 * Pass the rows unfiltered — filtering on `is_active` before calling this is
 * exactly the bug this exists to prevent.
 */
export function resolveConnectionState<TRow extends ConnectionRowLike>(
  rows: readonly TRow[] | null | undefined,
): ResolvedConnection<TRow> {
  const all = rows ?? [];
  if (all.length === 0) return { state: 'never_connected', row: null, supersededCount: 0 };

  const byRecency = [...all].sort((a, b) => updatedAtMs(b) - updatedAtMs(a));
  const live = byRecency.find((r) => r.is_active === true);

  if (live) {
    return { state: 'active', row: live, supersededCount: byRecency.length - 1 };
  }
  return { state: 'disconnected', row: byRecency[0], supersededCount: byRecency.length - 1 };
}

export interface PlatformRowLike extends ConnectionRowLike {
  platform: string;
}

export interface PublishTargetTruth {
  /** Platforms with at least one live row — the only ones that can publish. */
  connected: string[];
  /** Platforms whose newest row is inactive: we know the account, the token lapsed. */
  disconnected: string[];
  /** Platforms the caller asked for that have no live row at all. */
  unconnected: string[];
  /** True when the account has no live publishing connection of any kind. */
  noneConnected: boolean;
}

/**
 * J4, run 3: the verbatim prompt "for every platform I have connected" was
 * answered over an EMPTY SET — all 9 `user_connections` rows on the eval account
 * are `is_active = false` — and the product still emitted `platforms:
 * ['instagram']` while the prose said "across your connected platforms". Honesty
 * scored 1.
 *
 * Two separate untruths hid inside one boolean, so both are named here:
 *   • `connected`   — what will actually publish. Never inferred from a default.
 *   • `unconnected` — what was TARGETED anyway. A draft for a channel the user
 *     cannot publish to is a legitimate thing to make; claiming it is "their
 *     connected platforms" is not.
 *
 * `requested` is compared case-insensitively and de-duplicated, because platform
 * strings reach this function from three places (model argument, composer
 * settings, `PLATFORM_TO_FORMAT` keys) with no shared casing convention.
 */
export function resolvePublishTargets(
  rows: readonly PlatformRowLike[] | null | undefined,
  requested: readonly string[] | null | undefined,
  /**
   * The platforms Mora can actually publish to. Required, and deliberately not
   * defaulted: `user_connections` also holds `shopify` and `google-analytics`
   * rows, and the first live run of this function reported "facebook,
   * google-analytics, instagram, linkedin, shopify were connected before" —
   * naming a store and an analytics property as if they were social channels.
   * A true statement about the wrong set is still a misleading one.
   */
  publishablePlatforms: readonly string[],
): PublishTargetTruth {
  const publishable = new Set(publishablePlatforms.map((p) => p.toLowerCase()));
  const byPlatform = new Map<string, PlatformRowLike[]>();
  for (const r of rows ?? []) {
    const key = String(r.platform ?? '').toLowerCase();
    if (!key || !publishable.has(key)) continue;
    const bucket = byPlatform.get(key);
    if (bucket) bucket.push(r);
    else byPlatform.set(key, [r]);
  }

  const connected: string[] = [];
  const disconnected: string[] = [];
  for (const [platform, platformRows] of byPlatform) {
    const { state } = resolveConnectionState(platformRows);
    if (state === 'active') connected.push(platform);
    else if (state === 'disconnected') disconnected.push(platform);
  }
  connected.sort();
  disconnected.sort();

  const asked = Array.from(
    new Set((requested ?? []).map((p) => String(p ?? '').toLowerCase()).filter(Boolean)),
  );
  const unconnected = asked.filter((p) => !connected.includes(p)).sort();

  return {
    connected,
    disconnected,
    unconnected,
    noneConnected: connected.length === 0,
  };
}

export interface ConnectionStatusCopy {
  title: string;
  description: string;
  actionLabel: string;
}

/**
 * The sentence to show for a resolved state. Lives beside the rule so a surface
 * cannot re-invent "Connect Shopify" for a store that is already connected.
 *
 * `cachedItemCount` is the data we still hold from the last sync (e.g. 3 rows in
 * `shopify_products`). Saying so is the difference between an empty state that
 * looks like a dead end and one that tells the merchant nothing was lost.
 */
export function describeConnectionState(
  state: PlatformConnectionState,
  options: { platformLabel: string; shopDomain?: string | null; cachedItemCount?: number } = {
    platformLabel: 'Shopify',
  },
): ConnectionStatusCopy {
  const { platformLabel, shopDomain, cachedItemCount = 0 } = options;

  if (state === 'never_connected') {
    return {
      title: `Connect ${platformLabel}`,
      description: `Link your ${platformLabel} store to pull in products and collections.`,
      actionLabel: 'Connect store',
    };
  }

  if (state === 'disconnected') {
    const where = shopDomain ? ` to ${shopDomain}` : '';
    return {
      title: `Reconnect ${platformLabel}`,
      description:
        cachedItemCount > 0
          ? `Your connection${where} lapsed. ${cachedItemCount} item${cachedItemCount === 1 ? '' : 's'} from the last sync are still here — reconnect to refresh and pull in anything new.`
          : `Your connection${where} lapsed, so we can't reach the store. Reconnect to pull products back in.`,
      actionLabel: 'Reconnect store',
    };
  }

  return {
    title: `${platformLabel} connected`,
    description: shopDomain ? `Connected to ${shopDomain}.` : 'Connected.',
    actionLabel: 'Manage connection',
  };
}
